import React, { useState } from 'react';
import {
  List,
  Datagrid,
  TextField,
  DateField,
  SearchInput,
  SelectInput,
  FunctionField,
  useRecordContext,
  BooleanField,
  useUpdate,
  useNotify,
  useRefresh,
  useDelete,
  EditButton,
  ShowButton,
  TopToolbar,
  DeleteWithConfirmButton,
} from 'react-admin';
import {
  NotificationType,
  NotificationPriority,
} from '../../generated/data-contracts';
import {
  Chip,
  IconButton,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button as MuiButton,
  Card,
  Typography,
} from '@mui/material';
import {
  MarkEmailRead,
  NotificationsOff,
  Add as AddIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';
import CustomCreateButton from '@/components/CustomCreateButton';

const NotificationListActions = () => (
  <TopToolbar>
    <CustomCreateButton
      variant='contained'
      label='Crear Notificación'
      icon={<AddIcon />}
      sx={{
        height: '40px',
        backgroundColor: 'primary.main',
        '&:hover': {
          backgroundColor: 'primary.dark',
        },
      }}
    />
  </TopToolbar>
);

// Filtros para la búsqueda
const notificationFilters = [
  <SearchInput
    key='search'
    source='q'
    placeholder='Buscar notificaciones...'
    alwaysOn
    size='small'
    sx={{ 
      minWidth: 200,
      mb: 0.5
    }}
  />,
  <SelectInput
    key='type'
    source='Type'
    label='Tipo'
    choices={[
      { id: NotificationType.Info, name: 'Información' },
      { id: NotificationType.Warning, name: 'Advertencia' },
      { id: NotificationType.Alert, name: 'Alerta' },
      { id: NotificationType.Success, name: 'Éxito' },
    ]}
    emptyText='Todos los tipos'
  />,
  <SelectInput
    key='priority'
    source='Priority'
    label='Prioridad'
    choices={[
      { id: NotificationPriority.Low, name: 'Baja' },
      { id: NotificationPriority.Medium, name: 'Media' },
      { id: NotificationPriority.High, name: 'Alta' },
      { id: NotificationPriority.Critical, name: 'Crítica' },
    ]}
    emptyText='Todas las prioridades'
  />,
  <SelectInput
    key='isActive'
    source='IsActive'
    label='Estado'
    choices={[
      { id: true, name: 'Activas' },
      { id: false, name: 'Inactivas' },
    ]}
    emptyText='Todas'
  />,
];

// renderiza el tipo con colores
const TypeField = () => {
  const record = useRecordContext();
  if (!record) return null;

  const typeColors = {
    [NotificationType.Info]: 'info',
    [NotificationType.Warning]: 'warning',
    [NotificationType.Alert]: 'error',
    [NotificationType.Success]: 'success',
  } as const;

  const typeLabels = {
    [NotificationType.Info]: 'Información',
    [NotificationType.Warning]: 'Advertencia',
    [NotificationType.Alert]: 'Alerta',
    [NotificationType.Success]: 'Éxito',
  };

  return (
    <Chip
      label={typeLabels[record.type as NotificationType] || record.type}
      color={typeColors[record.type as NotificationType] || 'default'}
      size='small'
    />
  );
};

// renderiza la prioridad con colores
const PriorityField = () => {
  const record = useRecordContext();
  if (!record) return null;

  const priorityColors = {
    [NotificationPriority.Low]: 'default',
    [NotificationPriority.Medium]: 'primary',
    [NotificationPriority.High]: 'warning',
    [NotificationPriority.Critical]: 'error',
  } as const;

  const priorityLabels = {
    [NotificationPriority.Low]: 'Baja',
    [NotificationPriority.Medium]: 'Media',
    [NotificationPriority.High]: 'Alta',
    [NotificationPriority.Critical]: 'Crítica',
  };

  return (
    <Chip
      label={
        priorityLabels[record.priority as NotificationPriority] ||
        record.priority
      }
      color={
        priorityColors[record.priority as NotificationPriority] || 'default'
      }
      size='small'
    />
  );
};

//  renderiza el estado activo
const ActiveField = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Chip
      label={record.isActive ? 'Activa' : 'Inactiva'}
      color={record.isActive ? 'success' : 'default'}
      size='small'
      variant={record.isActive ? 'filled' : 'outlined'}
    />
  );
};

// Componente para los botones de acción
const ActionButtons = () => {
  const record = useRecordContext();
  const [update] = useUpdate();
  const [deleteOne] = useDelete();
  const notify = useNotify();
  const refresh = useRefresh();
  const [dialogOpen, setDialogOpen] = useState(false);

  if (!record) return null;

  const handleMarkAsRead = async () => {
    try {
      await update(
        'notifications-admin',
        {
          id: record.id,
          data: {},
          previousData: record,
        },
        {
          mutationMode: 'pessimistic',
          onSuccess: () => {
            notify('Notificación marcada como leída', { type: 'success' });
            refresh();
          },
          onError: (error: any) => {
            notify(`Error: ${error.message}`, { type: 'error' });
          },
        }
      );
    } catch (error: any) {
      notify(`Error: ${error.message}`, { type: 'error' });
    }
  };

  const handleConfirmDisable = async () => {
    setDialogOpen(false);

    try {
      // Desactivar la notificación actualizando isActive = false
      await update(
        'notifications-admin',
        {
          id: record.id,
          data: {
            title: record.title,
            content: record.content,
            type: record.type,
            priority: record.priority,
            expiresAt: record.expiresAt,
            isActive: false, // Desactivar la notificación
          },
          previousData: record,
        },
        {
          mutationMode: 'pessimistic',
          onSuccess: () => {
            notify('Notificación desactivada correctamente', {
              type: 'success',
            });
            refresh();
          },
          onError: (error: any) => {
            notify(`Error al desactivar: ${error.message}`, { type: 'error' });
          },
        }
      );
    } catch (error: any) {
      notify(`Error al desactivar: ${error.message}`, { type: 'error' });
    }
  };

  return (
    <>
      <Box display='flex' gap={1}>
        <EditButton
          size='small'
          color='primary'
          sx={{ minWidth: 'auto', padding: '6px' }}
        />
        {!record.isRead && (
          <IconButton
            size='small'
            onClick={handleMarkAsRead}
            title='Marcar como leída'
            color='primary'
          >
            <MarkEmailRead />
          </IconButton>
        )}
        {record.isActive && (
          <IconButton
            size='small'
            onClick={() => setDialogOpen(true)}
            title='Desactivar notificación/alarma'
            color='warning'
          >
            <NotificationsOff />
          </IconButton>
        )}
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        aria-labelledby='confirm-disable-dialog-title'
        aria-describedby='confirm-disable-dialog-description'
      >
        <DialogTitle id='confirm-disable-dialog-title'>
          Desactivar Notificación
        </DialogTitle>
        <DialogContent>
          <DialogContentText id='confirm-disable-dialog-description'>
            ¿Estás seguro de que quieres desactivar esta notificación?
            <br />
            Esta acción marcará la notificación como leída y la eliminará
            permanentemente.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setDialogOpen(false)} color='primary'>
            Cancelar
          </MuiButton>
          <MuiButton
            onClick={handleConfirmDisable}
            color='warning'
            variant='contained'
          >
            Desactivar
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const NotificationsList: React.FC = () => (
  <Card elevation={1} sx={{ m: 2, p: 2 }}>
    <Box sx={{ p: 3, pt: 2, pb: 2.5, borderBottom: 1, borderColor: 'divider' }}>
      <Typography
        variant='h5'
        component='h1'
        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <NotificationsIcon color='primary' />
        Gestión de Notificaciones
      </Typography>
    </Box>
    <List
      title={false}
      sort={{ field: 'createdAt', order: 'DESC' }}
      perPage={25}
      filters={notificationFilters}
      resource='notifications-admin'
      actions={<NotificationListActions />}
      sx={{ '& .RaList-main': { mt: 0 } }}
    >
      <Datagrid
        rowClick='show'
        sx={{ '& .RaDatagrid-headerCell': { fontWeight: 'bold' } }}
      >
        <TextField source='id' label='ID' />
        <TextField source='title' label='Título' />
        <FunctionField label='Tipo' render={() => <TypeField />} />
        <FunctionField label='Prioridad' render={() => <PriorityField />} />
        <FunctionField label='Estado' render={() => <ActiveField />} />
        <BooleanField source='isRead' label='Leída' />
        <DateField source='createdAt' label='Fecha de creación' showTime />
        <DateField source='expiresAt' label='Expira' showTime />
        <ShowButton label={false}/>
        <EditButton label={false}/>
        <DeleteWithConfirmButton
          confirmTitle='Confirmar eliminación'
          confirmContent={`¿Seguro que quieres eliminar la notificación ? Esta acción no se puede deshacer.`}
          mutationMode='pessimistic'
          label={false}
        />
      </Datagrid>
    </List>
  </Card>
);
